import type {
  PharmaCompanyChip,
  PrescriptionCard,
  PrescriptionSortKey,
} from '@/entities/performance/api/performance.api';
import { PrescriptionLineCard } from '@/entities/performance/components/PrescriptionLineCard';
import {
  useClinicDrugs,
  useClinicPharmaCompanies,
} from '@/entities/performance/hooks/usePerformanceQueries';
import { getLineBadge, monthOf } from '@/entities/performance/lib/performance';
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
} from '@/shared/components/StateViews';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/utils';
import { formatWon } from '@/shared/utils/format';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { ChipMenu, type ChipOption } from './PerformanceParts';

const SORT_OPTIONS: ChipOption<PrescriptionSortKey>[] = [
  { value: 'CONFIRMED_AMOUNT_DESC', label: '확정 금액 높은순' },
  { value: 'ESTIMATED_AMOUNT_DESC', label: '예상 금액 높은순' },
  { value: 'OUT_HOSPITAL_QUANTITY_DESC', label: '처방량 많은순' },
  { value: 'COMMISSION_RATE_DESC', label: '수수료율 높은순' },
];

/** 상태 필터 값 — 배지가 없는 라인(처방 · 정산 모두 승인)은 "정상" */
const NORMAL = '정상';
const lineStatus = (line: PrescriptionCard) =>
  getLineBadge(line)?.label ?? NORMAL;

/** 처방 카드 2단 그리드 + [더 보기] */
const LineGrid = ({
  lines,
  clinicName,
  hasMore,
  loadingMore,
  onMore,
}: {
  lines: PrescriptionCard[];
  clinicName?: string;
  hasMore: boolean;
  loadingMore: boolean;
  onMore: () => void;
}) => (
  <>
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {lines.map((line) => (
        <PrescriptionLineCard
          key={line.prescriptionId}
          line={line}
          clinicName={clinicName}
        />
      ))}
    </div>
    {hasMore && (
      <div className="mt-4 flex justify-center">
        <Button
          variant="secondary"
          size="md"
          loading={loadingMore}
          onClick={onMore}
        >
          더 보기
        </Button>
      </div>
    )}
  </>
);

/** 제약사 ▾ · 칩에서 고를 수 있는 제약사 — 제약사 없는 정산(null)은 필터로 보내지 않는다 */
const selectableChips = (chips: PharmaCompanyChip[] = []) =>
  chips.filter(
    (c): c is PharmaCompanyChip & { pharmaCompanyId: number } =>
      c.pharmaCompanyId !== null,
  );

/**
 * M-09 전체 탭 — 의약품 검색 · 정렬 · 제약사는 서버, 상태는 불러온 카드에서 클라이언트로 거른다
 */
export const AllTab = ({
  ym,
  clinicId,
  clinicName,
}: {
  ym: string;
  clinicId: number;
  clinicName?: string;
}) => {
  const [keyword, setKeyword] = useState('');
  const drugName = useDebouncedValue(keyword.trim());
  const [sortKey, setSortKey] = useState<PrescriptionSortKey>(
    'CONFIRMED_AMOUNT_DESC',
  );
  const [pharmaId, setPharmaId] = useState<number>();
  const [status, setStatus] = useState<string>();

  const pharmas = useClinicPharmaCompanies(ym, clinicId);
  const drugs = useClinicDrugs(ym, clinicId, {
    drugName: drugName || undefined,
    sortKeys: [sortKey],
    pharmaCompanyIds: pharmaId === undefined ? undefined : [pharmaId],
  });

  const lines = useMemo(
    () => drugs.data?.pages.flatMap((p) => p.content) ?? [],
    [drugs.data],
  );
  const total = drugs.data?.pages[0]?.totalCount ?? 0;
  const statusOptions = useMemo(
    () =>
      Array.from(new Set(lines.map(lineStatus))).map((s) => ({
        value: s,
        label: s,
      })),
    [lines],
  );
  const visible = status
    ? lines.filter((l) => lineStatus(l) === status)
    : lines;

  // 상태는 서버 필터가 없어 클라이언트로 거른다. 고르면 남은 페이지를 모두 불러와
  // 건수와 목록이 어긋나지 않게 한다 (병의원 한 곳 · 한 달 단위라 양이 많지 않다)
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = drugs;
  useEffect(() => {
    if (status && hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [status, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-3 lg:h-[42px] lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
          <label className="flex h-[42px] w-full items-center justify-between gap-2 rounded-[9px] border border-[#e5e5e5] bg-[#f7f7f7] px-[13px] sm:w-80">
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="의약품명을 입력하세요"
              className="w-full min-w-0 bg-transparent text-[13px] leading-5 text-ink outline-none placeholder:text-[#a8a8a8]"
            />
            <Search className="size-3.5 shrink-0 text-[#a8a8a8]" aria-hidden />
          </label>
          <p className="text-[13px] leading-5 font-bold whitespace-nowrap text-ink">
            {ym.slice(0, 4)}년 {monthOf(ym)}월 처방 (
            {status ? visible.length : total})
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ChipMenu
            placeholder="정렬"
            value={sortKey}
            options={SORT_OPTIONS}
            onChange={(v) => v && setSortKey(v)}
          />
          <ChipMenu
            placeholder="제약사"
            allLabel="전체 제약사"
            value={pharmaId}
            options={selectableChips(pharmas.data?.pharmaCompanies).map(
              (p) => ({
                value: p.pharmaCompanyId,
                label: p.pharmaCompanyName ?? `제약사 ${p.pharmaCompanyId}`,
              }),
            )}
            onChange={setPharmaId}
          />
          <ChipMenu
            placeholder="상태"
            allLabel="전체 상태"
            value={status}
            options={statusOptions}
            onChange={setStatus}
          />
        </div>
      </div>
      <div className="mt-3.5">
        {drugs.isPending ? (
          <LoadingBlock className="h-[215px]" />
        ) : drugs.isError ? (
          <ErrorState onRetry={() => void drugs.refetch()} />
        ) : visible.length === 0 ? (
          <EmptyState
            message={
              drugName || pharmaId || status
                ? '조건에 맞는 처방 내역이 없어요.'
                : '이 달에 등록된 처방 내역이 없어요.'
            }
          />
        ) : (
          <LineGrid
            lines={visible}
            clinicName={clinicName}
            hasMore={!!drugs.hasNextPage}
            loadingMore={drugs.isFetchingNextPage}
            onMore={() => void drugs.fetchNextPage()}
          />
        )}
      </div>
    </div>
  );
};

const PharmaChip = ({
  selected,
  name,
  sub,
  onClick,
}: {
  selected: boolean;
  name: string;
  sub: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={cn(
      'flex shrink-0 items-center gap-[7px] rounded-[9px] border px-[13px] py-2 whitespace-nowrap',
      selected
        ? 'border-ink bg-ink'
        : 'border-[#e3e3e3] bg-surface hover:bg-subtle',
    )}
  >
    <span
      className={cn(
        'text-[12.5px] leading-[19px] font-medium',
        selected ? 'text-white' : 'text-ink-2',
      )}
    >
      {name}
    </span>
    <span
      className={cn(
        'text-[11.5px] leading-[17px]',
        selected ? 'text-[#c9c9c9]' : 'text-ink-7',
      )}
    >
      {sub}
    </span>
  </button>
);

/** 제약사가 없는 정산 칩 — URL ?pharma= 값 */
const NO_PHARMA = -1;

/**
 * M-17 제약사 탭 — 칩 · 합계는 GET /pharma-companies, 카드는 GET /drugs?pharmaCompanyIds=.
 * 처음 들어오면 금액 1위 제약사를 고른다. 같은 약의 정산 값이 카드마다 반복될 수 있어
 * 합계는 카드를 더하지 않고 칩 값을 쓴다.
 * 제약사 없는 정산(pharmaCompanyId null)은 칩과 합계에만 있고 카드가 없다.
 */
export const PharmaTab = ({
  ym,
  clinicId,
  clinicName,
}: {
  ym: string;
  clinicId: number;
  clinicName?: string;
}) => {
  const pharmas = useClinicPharmaCompanies(ym, clinicId);
  const chips = pharmas.data?.pharmaCompanies ?? [];
  const chipId = (c: PharmaCompanyChip) => c.pharmaCompanyId ?? NO_PHARMA;
  // 선택 제약사는 URL ?pharma= 에 둔다 (새로고침 · 공유 시 유지)
  // undefined = 아직 고르지 않음(1위 자동 선택), 0 = 전체, -1 = 제약사 없는 정산
  const search = useSearch({ strict: false }) as { pharma?: number };
  const navigate = useNavigate();
  const setPicked = (id: number) =>
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, pharma: id }),
      replace: true,
    });
  // 다른 병의원 · 달로 옮겨 URL의 제약사가 목록에 없으면 1위 자동 선택으로 되돌린다
  const picked =
    search.pharma === undefined ||
    (search.pharma !== 0 && !chips.some((c) => chipId(c) === search.pharma))
      ? undefined
      : search.pharma;
  const selectedKey = picked ?? (chips[0] ? chipId(chips[0]) : 0);
  const selected = chips.find((c) => chipId(c) === selectedKey);
  const isAll = selectedKey === 0;

  const drugs = useClinicDrugs(
    ym,
    clinicId,
    {
      sortKeys: ['CONFIRMED_AMOUNT_DESC'],
      pharmaCompanyIds: isAll ? undefined : [selectedKey],
    },
    // 제약사 없는 정산 · 정산만 있는 제약사는 카드가 없다
    {
      enabled:
        pharmas.isSuccess &&
        selectedKey !== NO_PHARMA &&
        (isAll || (selected?.drugCount ?? 0) > 0),
    },
  );
  const lines = drugs.data?.pages.flatMap((p) => p.content) ?? [];

  if (pharmas.isPending) return <LoadingBlock className="h-[300px]" />;
  if (pharmas.isError)
    return <ErrorState onRetry={() => void pharmas.refetch()} />;

  const total = pharmas.data;
  const summary = isAll ? total : selected;
  const noCards =
    !isAll && (selectedKey === NO_PHARMA || (selected?.drugCount ?? 0) === 0);

  return (
    <div className="flex flex-col">
      {/* 칩이 넘치면 줄바꿈 (Figma) */}
      <div className="flex flex-wrap gap-2">
        <PharmaChip
          selected={isAll}
          name="전체"
          sub={`${total.drugCount}품목`}
          onClick={() => setPicked(0)}
        />
        {chips.map((p) => (
          <PharmaChip
            key={chipId(p)}
            selected={chipId(p) === selectedKey}
            name={p.pharmaCompanyName ?? '제약사 정보 없음'}
            sub={formatWon(p.confirmedAmount)}
            onClick={() => setPicked(chipId(p))}
          />
        ))}
      </div>

      <div className="mt-[18px] flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <h3 className="text-[15px] leading-6 font-bold text-ink">
            {isAll
              ? '전체'
              : (selected?.pharmaCompanyName ?? '제약사 정보 없음')}
          </h3>
          {summary && (
            <span className="rounded-badge bg-muted px-[7px] py-[3px] text-[10px] leading-[15px] font-medium text-ink-4">
              {summary.drugCount}품목
            </span>
          )}
        </div>
        <p className="flex flex-wrap items-center gap-x-3 text-[12.5px] leading-[19px] font-bold">
          <span className="text-ink-3">
            예상 금액 {formatWon(summary?.estimatedAmount)}
          </span>
          <span className="text-success">
            확정 금액 {formatWon(summary?.confirmedAmount)}
          </span>
        </p>
      </div>

      <div className="mt-3.5">
        {noCards ? (
          <EmptyState
            message={
              selectedKey === NO_PHARMA
                ? '제약사 정보가 없는 정산이라 처방 카드가 없어요.'
                : '정산만 있고 처방 카드는 없어요.'
            }
          />
        ) : drugs.isPending ? (
          <LoadingBlock className="h-[215px]" />
        ) : drugs.isError ? (
          <ErrorState onRetry={() => void drugs.refetch()} />
        ) : lines.length === 0 ? (
          <EmptyState message="이 제약사의 처방 내역이 없어요." />
        ) : (
          <LineGrid
            lines={lines}
            clinicName={clinicName}
            hasMore={!!drugs.hasNextPage}
            loadingMore={drugs.isFetchingNextPage}
            onMore={() => void drugs.fetchNextPage()}
          />
        )}
      </div>
    </div>
  );
};
